"use client";

import { useRef, useEffect } from "react";

export interface EmojiPickerWrapperProps {
  onSelect: (emoji: string) => void;
}

/**
 * Hosts the emoji-mart Picker, which is a custom element with its own
 * shadow DOM. We append it imperatively, so we MUST remove it imperatively
 * on unmount — otherwise React's reconciler races against the picker's
 * own teardown and throws "Cannot read properties of null (reading
 * 'removeChild')" (PostHog confirmed this on /team-chat).
 */
export function EmojiPickerWrapper({ onSelect }: EmojiPickerWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    let picker: HTMLElement | null = null;

    import("emoji-mart").then(({ Picker }) => {
      if (cancelled || !containerRef.current) return;
      picker = new Picker({
        onEmojiSelect: (emoji: any) => onSelectRef.current(emoji.native),
        set: "native",
        // NOT "auto": that follows the OS, and this app's dark mode is the
        // `.dark` class the `dark:` utilities key off — an OS set to dark gave
        // a black picker inside a light app. Read the same signal the rest of
        // the UI reads.
        theme: document.documentElement.classList.contains("dark") ? "dark" : "light",
        previewPosition: "none",
        skinTonePosition: "search",
        // dynamicWidth makes the grid fill its container instead of sitting at
        // a fixed perLine width — otherwise the picker is narrower than the
        // tab bar above it and leaves a gutter down the right.
        dynamicWidth: true,
        maxFrequentRows: 2,
        navPosition: "bottom",
        searchPosition: "sticky",
      }) as unknown as HTMLElement;
      el.appendChild(picker);
    });

    return () => {
      cancelled = true;
      // Remove the picker BEFORE React commits the wrapper's deletion so the
      // picker's own teardown doesn't null out parent refs mid-sweep.
      if (picker && picker.parentNode === el) {
        try {
          el.removeChild(picker);
        } catch {
          // node may already be detached during a fast unmount — ignore.
        }
      }
      picker = null;
    };
  }, []);

  // The host must be full-width for dynamicWidth to have anything to fill.
  return <div ref={containerRef} className="w-full [&>em-emoji-picker]:w-full" />;
}
